import { useQuery } from '@apollo/client';
import { generateItemsWithPromosAmounts, usePaginationSearch, useRouter } from '@application/utils';
import { graphql } from '@gql/gql';
import type { ArticlesGetArticlesQuery, ArticlesGetArticlesQueryVariables } from '@gql/graphql';
import { SortOrder } from '@gql/graphql';
import { useMemo } from 'react';
import { PAGINATED_SIZE } from '../constants';
import { useArticlesWhereBuilder } from './useArticlesWhereBuilder';
import { useSearch } from './useSearch';

export const ARTICLES_GET_ARTICLES = graphql(`
  query ArticlesGetArticles(
    $where: ArticleWhereInput
    $orderBy: [ArticleOrderByWithRelationAndSearchRelevanceInput!]
    $take: Int
    $skip: Int
    $articleCardInfoTranslationsWhere: ArticleTranslationsWhereInput
  ) {
    aggregateArticle(where: $where) {
      _count {
        _all
      }
    }
    findManyArticle(where: $where, orderBy: $orderBy, take: $take, skip: $skip) {
      ...ArticleCardInfo
    }
  }
`);

export const useGetArticles = () => {
  const { articlesWhereBuilder } = useArticlesWhereBuilder();
  const { valuesFromSearch } = useSearch();
  const { currentPage } = usePaginationSearch();
  const { languageCode } = useRouter();

  const variables = useMemo((): ArticlesGetArticlesQueryVariables => {
    return {
      where: articlesWhereBuilder(valuesFromSearch),
      take: generateItemsWithPromosAmounts(PAGINATED_SIZE).itemAmount,
      skip: generateItemsWithPromosAmounts(PAGINATED_SIZE).itemAmount * currentPage,
      orderBy: [{ createdAt: SortOrder.desc }],
      articleCardInfoTranslationsWhere: {
        languageCode: { equals: languageCode },
        active: { equals: true },
      },
    };
  }, [articlesWhereBuilder, currentPage, languageCode, valuesFromSearch]);

  const { data, error, loading, fetchMore, refetch } = useQuery<
    ArticlesGetArticlesQuery,
    ArticlesGetArticlesQueryVariables
  >(ARTICLES_GET_ARTICLES, {
    variables,
    notifyOnNetworkStatusChange: true,
  });

  return {
    articles: data?.findManyArticle,
    // eslint-disable-next-line no-underscore-dangle
    articlesCount: data?.aggregateArticle?._count?._all ?? 0,
    articlesError: error,
    articlesLoading: loading,
    articlesVariables: variables,
    articlesFetchMore: fetchMore,
    articlesRefetch: refetch,
  };
};
